import { CONTENTBOX_PATCH } from './../common/constants';
import { ContentBox, Campaign, Maybe, Scalars } from '../generated/contentful-generated-types';
import { ExtendedContentBox, PatchAndDiscount } from './contentBox';
import { isCategoryPage, showForUserMultiple } from './contentful';
import { MenuItem } from '../components/menu/MenuUtils';
import { selectAndSortApplicableCampaign } from '../hooks/useCampaign';

export function isCampaignForCurrentCountry(campaign: Campaign | null) {
  return process.env.GATSBY_LOCALE && campaign?.country?.toLowerCase().includes(process.env.GATSBY_LOCALE);
}

export const applyCampaign = (item: ContentBox) => {
  const relatedCampaign = item?.linkedFrom?.campaignCollection?.items.find(isCampaignForCurrentCountry) ?? undefined;
  return applyCampaignOnContentBox(item, relatedCampaign);
};

export const applyCampaigns = (items: (ContentBox | null)[], campaigns: Campaign[]) =>
  items
    // ignore unresolved links to ContentBox items as well as ContentBox items with no content
    .filter((item: ContentBox | null): item is ContentBox => item != null && item.content != null)
    .map(item => {
      return {
        ...item,
        patchAndDiscount: applyAllCampaignOnContentBox(item.sys.id, item.pricePromo, campaigns)
      };
    })
    .map(propagatePatchAndDiscountPercentage);

export const applyCampaignOnContentBox = (item: ContentBox, campaign?: Campaign): ExtendedContentBox => {
  const thumbnailPatch = campaign?.thumbnailPatch ?? undefined;

  // handling of Patch special value 'Null' (value used to prevent automatic Contentful locale fallback)
  const thumbnailPatchWithNullHandling = thumbnailPatch === CONTENTBOX_PATCH.NULL ? undefined : thumbnailPatch;
  const showCampaignOnlyForMultiple = campaign?.showOnlyForMultiple?.filter((item): item is string => item !== null) ?? undefined;

  return {
    ...item,
    discountPercentage: campaign?.discountPercentage ?? undefined,
    patch: thumbnailPatchWithNullHandling,
    landingPagePriceTag: campaign?.lpPriceTag ?? undefined,
    thumbnailPriceTag: (campaign?.thumbnailPriceTag && item.pricePromo) || campaign?.thumbnailPriceTag || item.thumbnailPriceTag || undefined,
    showCampaignOnlyFor: campaign?.showOnlyFor ?? undefined,
    showCampaignOnlyForMultiple
  };
};

const applyAllCampaignOnContentBox = (itemId: string, pricePromo: string, campaigns?: Campaign[]): PatchAndDiscount[] | undefined => {
  const promo = campaigns
    ?.filter(campaign => campaign?.listCollection?.items?.find(campaignItem => itemId === campaignItem?.sys.id))
    ?.map(campaign => {
      const { discountPercentage, thumbnailPatch, thumbnailPriceTag, lpPriceTag, showOnlyFor, showOnlyForMultiple } = campaign;
      if (!discountPercentage && !thumbnailPatch && !thumbnailPriceTag && !lpPriceTag) return;

      return {
        discountPercentage: discountPercentage ?? undefined,
        patch: thumbnailPatch && thumbnailPatch !== CONTENTBOX_PATCH.NULL ? thumbnailPatch : undefined,
        landingPagePriceTag: lpPriceTag ?? undefined,
        thumbnailPriceTag: (thumbnailPriceTag && pricePromo) || thumbnailPriceTag || undefined,
        showCampaignOnlyFor: showOnlyFor ?? undefined,
        showCampaignOnlyForMultiple: showOnlyForMultiple ?? undefined
      };
    });
  const uniqueIDs = new Set();
  const uniquePromo = promo?.filter(item => {
    const isDuplicate = uniqueIDs.has(item);
    uniqueIDs.add(item);
    return !isDuplicate;
  });
  //return uniquePromo !== [] && uniquePromo && uniquePromo.length > 0 ? (uniquePromo as PatchAndDiscount[]) : undefined;
  return uniquePromo && Array.isArray(uniquePromo) && uniquePromo.length > 0 ? (uniquePromo as PatchAndDiscount[]) : undefined;
};

/**
 * Propagate discountPercentage, PROMO patch, PRICEDOWN patch, from child to parents category
 * Propagation priority is : discountPercentage, PROMO patch, PRICEDOWN patch
 * Biggest discountPercentage is propagated
 */
const propagatePatchAndDiscountPercentage = (item: ExtendedContentBox): ExtendedContentBox => {
  const content = item.content;

  // Propagation only if content is CategoryPage
  if (content == null || !isCategoryPage(content)) {
    return item;
  }

  const childrenContentBoxes = content.itemsCollection?.items.filter((item): item is ExtendedContentBox => item != null) || [];

  // PROMO and PRICEDOWN patches propagation
  const childrenPatches = childrenContentBoxes.map(item => item.patch).filter((item): item is string => item != null);
  const allPatches = item.patch ? [item.patch, ...childrenPatches] : childrenPatches;

  if (allPatches.includes(CONTENTBOX_PATCH.PROMO)) {
    return {
      ...item,
      patch: CONTENTBOX_PATCH.PROMO
    };
  }

  if (allPatches.includes(CONTENTBOX_PATCH.PRICEDOWN)) {
    return {
      ...item,
      patch: CONTENTBOX_PATCH.PRICEDOWN
    };
  }
  return item;
};

export const getPatchAndDiscountToDisplayInMenu = (
  currentUserReseller: boolean,
  currentUserLoggedIn: boolean,
  currentUserRegisteredNonBuyer: boolean,
  patchAndDiscount?: PatchAndDiscount[]
): PatchAndDiscount | undefined => {
  // If no promo found
  if (!patchAndDiscount) return;

  // Get promo with showOnlyFor
  const patchAndDiscountToDisplay: PatchAndDiscount[] = patchAndDiscount
    ?.filter(patchInfo => patchInfo.patch === 'Promo')
    ?.filter(promo => showForUserMultiple(promo?.showCampaignOnlyForMultiple, currentUserReseller, currentUserLoggedIn, currentUserRegisteredNonBuyer));

  // sort promo with discount percentage, if none return first patchWithDiscount
  const patchWithDiscount = patchAndDiscountToDisplay
    .filter(patch => patch?.discountPercentage !== undefined)
    ?.sort((a, b) => b.discountPercentage! - a.discountPercentage!);
  return patchWithDiscount[0] || patchAndDiscountToDisplay[0];
};

const getAllDiscountPercentages = (
  currentUserReseller: boolean,
  currentUserLoggedIn: boolean,
  currentUserRegisteredNonBuyer: boolean,
  patchAndDiscount?: PatchAndDiscount[]
): number | undefined => {
  if (!patchAndDiscount || patchAndDiscount.length < 1) return;
  const patchAndDiscountWithDiscountPercentage = patchAndDiscount
    ?.map(patchAndDiscount => {
      return showForUserMultiple(patchAndDiscount.showCampaignOnlyForMultiple, currentUserReseller, currentUserLoggedIn, currentUserRegisteredNonBuyer)
        ? patchAndDiscount?.discountPercentage
        : undefined;
    })
    .filter((item: any): item is number => item !== undefined && item > 0 && item < 100);
  return patchAndDiscountWithDiscountPercentage ? Math.max(...patchAndDiscountWithDiscountPercentage) : undefined;
};

export const getMaxMenuDiscountPercentage = (
  item: MenuItem,
  currentUserReseller: boolean,
  currentUserLoggedIn: boolean,
  currentUserRegisteredNonBuyer: boolean
): number | undefined => {
  if (!item?.children) return getAllDiscountPercentages(currentUserReseller, currentUserLoggedIn, currentUserRegisteredNonBuyer, item.patchAndDiscount);

  // DiscountPercentage propagation
  const childrenDiscountPercentages = item?.children
    ?.map((item: any) => getAllDiscountPercentages(currentUserReseller, currentUserLoggedIn, currentUserRegisteredNonBuyer, item.patchAndDiscount))
    ?.filter((item: any) => item && typeof item === 'number' && item > 0 && item < 100) as number[];

  const itemPatchAndDiscountMaxPercentage = getAllDiscountPercentages(
    currentUserReseller,
    currentUserLoggedIn,
    currentUserRegisteredNonBuyer,
    item.patchAndDiscount
  ) as number;

  const childrenHaveDiscountPercentage = childrenDiscountPercentages && childrenDiscountPercentages?.length >= 1;
  const itemHasDiscountPercentage = itemPatchAndDiscountMaxPercentage && itemPatchAndDiscountMaxPercentage > 0 && itemPatchAndDiscountMaxPercentage < 100;
  if (itemHasDiscountPercentage && childrenHaveDiscountPercentage) {
    return Math.max(itemPatchAndDiscountMaxPercentage, ...childrenDiscountPercentages);
  } else if (!itemHasDiscountPercentage && childrenHaveDiscountPercentage) {
    return Math.max(...childrenDiscountPercentages);
  } else if (itemHasDiscountPercentage && !childrenHaveDiscountPercentage) {
    return Math.max(itemPatchAndDiscountMaxPercentage);
  }
};
export type SimpleCampaign = {
  name?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  thumbnailPatch?: Maybe<Scalars['String']>;
  thumbnailPriceTag?: Maybe<Scalars['JSON']>;
  lpPriceTag?: Maybe<Scalars['JSON']>;
  discountPercentage?: Maybe<Scalars['Int']>;
  showOnlyForMultiple?: Maybe<Array<Maybe<Scalars['String']>>>;
};

export const createObjectLinkingContentfulIdsToCampaigns = (campaigns: Maybe<Campaign>[]) => {
  const idsToCampaigns: { [key: string]: SimpleCampaign[] } = {};

  const applicableCampaigns = selectAndSortApplicableCampaign(campaigns);
  if (!applicableCampaigns) return undefined;
  applicableCampaigns.forEach(campaign => {
    const entries = campaign?.listCollection?.items;
    if (!campaign) return;
    const { name, country, thumbnailPatch, thumbnailPriceTag, lpPriceTag, discountPercentage, showOnlyForMultiple }: SimpleCampaign = campaign;
    const simpleCampaign: SimpleCampaign = { name, country, thumbnailPatch, thumbnailPriceTag, lpPriceTag, discountPercentage, showOnlyForMultiple };
    entries?.forEach(entry => {
      const contentId = entry?.sys?.id;
      if (!contentId) return;
      if (!idsToCampaigns[contentId]) {
        idsToCampaigns[contentId] = [];
      }
      idsToCampaigns[contentId].push(simpleCampaign);
    });
  });
  return idsToCampaigns;
};
